"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  PhoneCall, 
  MessageSquare, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  CheckCircle,
  Clock
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const CallbackPage = () => {
  const [formStatus, setFormStatus] = useState("idle"); // idle, submitting, success

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus("submitting");
    // Simulate API call
    setTimeout(() => {
      setFormStatus("success");
    }, 1500);
  };

  return (
    <div className="bg-white min-h-screen font-jakarta">
      <section className="py-20 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
        {/* Left Side: Content */}
        <div className="flex-1 space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex items-center gap-2 bg-[#FFAE00]/10 text-[#FFAE00] px-4 py-2 rounded-full font-bold text-sm"
          >
            <PhoneCall className="w-4 h-4" /> REQUEST A CALL
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bricolage font-bold text-[#063258] leading-tight">
            Let&apos;s talk about your <span className="text-[#FFAE00]">next breakthrough.</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Not sure which trip is right for you? Want to know more about the mentors? Drop your details and our team will call you back within 2 business hours.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
            <div className="flex gap-4">
              <div className="bg-gray-50 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-[#063258]" />
              </div>
              <div>
                <h4 className="font-bold">Fast Response</h4>
                <p className="text-gray-500 text-sm">Calls within 2-4 hours</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-gray-50 p-3 rounded-xl">
                <MapPin className="w-6 h-6 text-[#063258]" />
              </div>
              <div>
                <h4 className="font-bold">Trip Advice</h4>
                <p className="text-gray-500 text-sm">Personalized curation</p>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-100">
            <p className="font-bold text-[#063258] mb-4">Prefer instant chat?</p>
            <Link href="https://wa.me/917982579691" target="_blank">
              <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-50 px-8 py-6 rounded-xl font-bold text-lg flex gap-2">
                <MessageSquare className="w-5 h-5" /> Message on WhatsApp
              </Button>
            </Link>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="flex-1 w-full max-w-lg">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl shadow-[#063258]/10 border border-gray-100"
          >
            {formStatus === "success" ? (
              <div className="text-center py-10 space-y-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-3xl font-bricolage font-bold text-[#063258]">All Set!</h2>
                <p className="text-gray-600 px-4">
                  We&apos;ve received your request. One of our travel curators will call you shortly.
                </p>
                <Button 
                  onClick={() => setFormStatus("idle")}
                  variant="link" 
                  className="text-[#FFAE00] font-bold"
                >
                  Need to send another request?
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-2xl font-bricolage font-bold text-[#063258] mb-6">Your Details</h3>
                
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input 
                      required
                      type="text" 
                      placeholder="e.g. Rahul Sharma"
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#FFAE00] focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input 
                      required
                      type="tel" 
                      placeholder="e.g. +91 98765 43210"
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#FFAE00] focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Topic (Optional)</label>
                  <select className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#FFAE00] focus:border-transparent outline-none transition-all appearance-none cursor-pointer">
                    <option>General Trip Inquiry</option>
                    <option>Founder Cohort Details</option>
                    <option>Custom Corporate Outing</option>
                    <option>Booking Assistance</option>
                  </select>
                </div>

                <Button 
                  disabled={formStatus === "submitting"}
                  type="submit" 
                  className="w-full bg-[#FFAE00] hover:bg-orange-500 text-white rounded-2xl py-8 text-xl font-bold shadow-lg shadow-orange-200 transition-all"
                >
                  {formStatus === "submitting" ? "Sending..." : "Request Call Back"}
                </Button>
                <p className="text-center text-xs text-gray-400 mt-4">
                  By clicking this, you agree to our Terms and Privacy Policy.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CallbackPage;
